import { NEXT_BTN_ATTRIBUTES, PREVIOUS_BTN_ATTRIBUTES } from '@app/constants'
import {
  CurrentTestState,
  CurrentUserAssessmentState,
  moveToNextItem,
  moveToPreviousItem,
} from '@app/storage'
import { NavigationMode } from '@app/types'
import { Box, Button, styled } from '@mui/material'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { ScreenReaderInfo } from '@app/components/common/ScreenReaderInfo'
import { QuestionContextMenuContext } from '@app/components/common/QuestionContextMenuContext'
import { NavAnnounceState } from '@app/components/common/ScreenReaderAnnouncer'
import { isSingleQuestionWritingTest } from '@app/helpers/isSingleQuestionWritingTest'

type Props = {
  reviewMode?: boolean
}

const InteractionLayoutFooter = ({ reviewMode = false }: Props) => {
  const { currentItemResponse, currentTestPart, itemsResponses } = useSnapshot(CurrentTestState)
  const { program, subject } = useSnapshot(CurrentUserAssessmentState)
  const { markedItems } = useContext(QuestionContextMenuContext)
  const currentItemIndex = itemsResponses.findIndex(item => item.id === currentItemResponse?.id)
  const { modalOpen } = useSnapshot(NavAnnounceState)
  const [moveAnnouncement, setMoveAnnouncement] = useState('')
  const prevItemIndexRef = useRef<number>()
  const navigationMode = currentTestPart?.navigationMode
  const isTestFinished = currentTestPart?.isFinished

  // Latest values read when the debounced announce fires — not effect deps
  // (markedItems is a fresh array each render; we want the settled question).
  const announceDataRef = useRef({
    currentItemIndex,
    reviewMode,
    markedItems,
    currentItemId: currentItemResponse?.itemId,
  })
  announceDataRef.current = {
    currentItemIndex,
    reviewMode,
    markedItems,
    currentItemId: currentItemResponse?.itemId,
  }

  // a11y: popup aria-hides this footer, so ChromeVox replays this region's text
  // on close — clear it on open so there's nothing stale; the popup self-announces.
  useEffect(() => {
    if (modalOpen) setMoveAnnouncement('')
  }, [modalOpen])

  // a11y: announce Prev/Next + review-bubble navigation (SC 4.1.3). Polite +
  // debounced so rapid moves announce only where you land; skip mount + popup-open.
  useEffect(() => {
    const prevIndex = prevItemIndexRef.current
    prevItemIndexRef.current = currentItemIndex
    const isRealNavigation =
      prevIndex !== undefined &&
      prevIndex >= 0 &&
      currentItemIndex >= 0 &&
      prevIndex !== currentItemIndex
    if (!isRealNavigation || NavAnnounceState.modalOpen) return

    const timerId = window.setTimeout(() => {
      // a11y: popup opened mid-debounce — don't re-seed the now-hidden region.
      if (NavAnnounceState.modalOpen) return
      const { currentItemIndex: index, reviewMode: rm, markedItems: mi, currentItemId } = announceDataRef.current
      if (index < 0) return
      const markedSuffix = !rm && !!currentItemId && mi.includes(currentItemId) ? ', marked for review' : ''
      setMoveAnnouncement(`You moved to question ${index + 1}${markedSuffix}.`)
    }, 400)
    return () => window.clearTimeout(timerId)
  }, [currentItemIndex])

  const disableControls = useMemo(() => {
    return (
      !currentItemResponse?.isResponseValid && navigationMode === NavigationMode.linear && !isTestFinished
    )
  }, [currentItemResponse, isTestFinished, navigationMode])

  const disablePrevious = useMemo(
    () => navigationMode !== NavigationMode.linear || isTestFinished,
    [isTestFinished, navigationMode],
  )

  const handlePreviousButtonClick = () => {
    moveToPreviousItem()
  }

  const handleNextButtonClick = () => {
    moveToNextItem()
  }

  // A single-question Writing test has nowhere to navigate. The strip itself stays
  // for visual consistency; only the controls go. Submit lives in ItemsNavigator,
  // so the test is still submittable without them.
  const hideNavigationControls = isSingleQuestionWritingTest({
    program,
    subject,
    itemCount: itemsResponses.length,
  })

  return (
    <FooterWrapper controlsHidden={hideNavigationControls}>
      <ScreenReaderInfo ariaLive={'polite'} ariaAtomic>
        {moveAnnouncement}
      </ScreenReaderInfo>
      {!hideNavigationControls && (
        <ButtonWrapper disablePrevious={disablePrevious} aria-label={'Questions navigation controls'}>
          {disablePrevious && (
            <Button
              disabled={disableControls || currentItemIndex === 0}
              aria-label='Previous question'
              variant='outlined'
              color='secondary'
              size='small'
              startIcon={<NavigationArrow icon={faArrowLeft} />}
              onClick={handlePreviousButtonClick}
              {...PREVIOUS_BTN_ATTRIBUTES}
            >
              {'Previous'}
            </Button>
          )}

          <Button
            disabled={disableControls || currentItemIndex === itemsResponses.length - 1}
            aria-label='Next question'
            variant='outlined'
            color='secondary'
            size='small'
            endIcon={<NavigationArrow icon={faArrowRight} />}
            onClick={handleNextButtonClick}
            {...NEXT_BTN_ATTRIBUTES}
          >
            {'Next'}
          </Button>
        </ButtonWrapper>
      )}
    </FooterWrapper>
  )
}

const FooterWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'controlsHidden',
})<{ controlsHidden?: boolean }>(
  ({ theme, controlsHidden = false }) => `
  padding: ${theme.spacing(2, 4)};
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid ${theme.palette.grey[100]};
  box-sizing: border-box;

  /* Without the controls the strip collapses to a thin rule, so hold the height
     they give it. Mirrors the theme's small-button metrics (1.188rem line-height
     + spacing(2) padding and 1px border, top and bottom) plus this strip's own
     padding and border-top. Applied only when the controls are hidden, so the
     normal footer keeps exactly the height it has today. */
  ${
    controlsHidden
      ? `min-height: calc(1.188rem + ${theme.spacing(4)} + 2px + ${theme.spacing(4)} + 1px);`
      : ''
  }

  ${[theme.breakpoints.down('mobile')]} {
    justify-content: unset;
    padding: ${theme.spacing(2)};
  }
`,
)

const ButtonWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'disablePrevious',
})<{ disablePrevious: boolean }>(
  ({ theme, disablePrevious }) => `
    display: flex;
    gap: ${theme.spacing(4)};

    ${[theme.breakpoints.down('mobile')]} {
      width: 100%;
      justify-content: ${disablePrevious ? 'space-between' : 'flex-end'}
    }
  `,
)

const NavigationArrow = styled(FontAwesomeIcon)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize + ' !important',
}))

export default InteractionLayoutFooter
